import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import type CompanyConfigurationModel from 'garaje/models/company-configuration';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import {
  GEOFENCE_RADIUS_DEFAULT,
  GEOFENCE_RADIUS_MAX,
  GEOFENCE_RADIUS_MIN,
} from 'garaje/pods/location-overview/manage/account-settings/utils/geofence-radius-bounds';
import type AjaxService from 'garaje/services/ajax';
import type AuthzService from 'garaje/services/authz';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type InactivityService from 'garaje/services/inactivity';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import type StatsigService from 'garaje/services/statsig';
import { Permission } from 'garaje/utils/ui-permissions';
import urlBuilder from 'garaje/utils/url-builder';
import { cached, localCopy } from 'tracked-toolbox';

interface LocationOption {
  location: SkinnyLocationModel;
  disabled: boolean;
}

enum INACTIVITY_TIME_OPTIONS {
  FIFTEEN_MINS = '15 minutes',
  THIRTY_MINS = '30 minutes',
  ONE_HR = '1 hour',
  TWO_HRS = '2 hours',
}

type MillisecondsToTimeType = {
  [key: number]: INACTIVITY_TIME_OPTIONS;
};

const MILLISECONDS_TO_TIME_MAP: MillisecondsToTimeType = {
  900000: INACTIVITY_TIME_OPTIONS.FIFTEEN_MINS,
  1800000: INACTIVITY_TIME_OPTIONS.THIRTY_MINS,
  3600000: INACTIVITY_TIME_OPTIONS.ONE_HR,
  7200000: INACTIVITY_TIME_OPTIONS.TWO_HRS,
};

interface PresenceSettingsResponse {
  data: {
    id: string;
    type: 'presence-settings';
    attributes: {
      'location-id': string;
      settings: {
        badge?: { enabled: boolean; schedule: null };
        wifi?: { enabled: boolean; schedule: null };
        sso?: { enabled: boolean; schedule: null };
        'geo-location'?: { enabled: boolean; schedule: null; 'geofence-radius-meters'?: number | null };
        manual?: { enabled: boolean; schedule: null };
        mdm?: { enabled: boolean; schedule: null };
      };
      'signout-settings'?: {
        enabled: boolean;
        'signal-absence-minutes'?: number;
        'signal-type-absence'?: {
          wifi?: number;
          badge?: number;
          sso?: number;
          mdm?: number;
          'geo-location'?: number;
        };
      };
      'updated-at': string;
      'updated-by': string | null;
    };
  };
}

class LocationOverviewManageAccountSettingsController extends Controller {
  @service declare ajax: AjaxService;
  @service declare abilities: AbilitiesService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare inactivity: InactivityService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare state: StateService;
  @service declare statsig: StatsigService;

  @tracked isInactivityBoxOpen: boolean = false;
  @localCopy('model.sessionInactivityTimeout', null) declare inactivityTimeout: number | null;

  @tracked isPresenceControlCenterOpen: boolean = false;
  @tracked selectedLocationId: string | null = null;
  @tracked macMappingsSearch = '';
  @tracked macMappingsSource = '';

  @tracked badgeEnabled: boolean = false;
  @tracked wifiEnabled: boolean = false;
  @tracked ssoEnabled: boolean = false;
  @tracked geoLocationEnabled: boolean = false;
  @tracked manualEnabled: boolean = false;
  @tracked mdmEnabled: boolean = false;
  @tracked isLoading: boolean = false;
  @tracked error: string | null = null;
  @tracked selectedPresenceTab: 'sign-in' | 'sign-out' = 'sign-in';

  // Aggregated auto sign-out configuration (any signal resets a single timer)
  @tracked absenceOfSignalsEnabled: boolean = false;
  @tracked absenceOfSignalsDuration: number = 3600000; // Default: 1 hour

  // Per-signal-type auto sign-out configuration (in UI order: badge, wifi, sso, mdm, geo-location)
  @tracked badgeAutoSignOutEnabled: boolean = false;
  @tracked badgeAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked wifiAutoSignOutEnabled: boolean = false;
  @tracked wifiAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked ssoAutoSignOutEnabled: boolean = false;
  @tracked ssoAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked mdmAutoSignOutEnabled: boolean = false;
  @tracked mdmAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked geoLocationAutoSignOutEnabled: boolean = false;

  // Per-location geofence radius (meters) — Unit 4 of EIP-3334.
  // Display default of 150 m matches the existing mobile fallback so behavior is unchanged
  // for locations whose server-side column is still NULL. Bounds mirror the server CHECK
  // constraint exactly (see utils/geofence-radius-bounds.ts).
  @tracked geoLocationRadius: number = GEOFENCE_RADIUS_DEFAULT;
  @tracked geoLocationRadiusError: string | null = null;

  declare model: CompanyConfigurationModel | undefined;

  get headerTitle(): string {
    const companyName = this.state.currentCompany?.name;
    return companyName ? `Account settings for ${companyName}` : 'Account settings';
  }

  get inactivityDescription(): INACTIVITY_TIME_OPTIONS | undefined {
    const sessionTimeout: number = this.model?.sessionInactivityTimeout || 7200000;
    return MILLISECONDS_TO_TIME_MAP[sessionTimeout];
  }

  get isInactivityEnabled(): boolean {
    const companyConfiguration = this.model;
    return Boolean(companyConfiguration?.sessionInactivityTimeout);
  }

  get inactivityOptions(): Array<number> {
    return [900000, 1800000, 3600000, 7200000];
  }

  get isSaveBtnDisabled(): boolean {
    return this.model?.sessionInactivityTimeout === this.inactivityTimeout;
  }

  @cached
  get availableLocations(): LocationOption[] {
    const locations = this.skinnyLocations.currentCompanyLocations || [];

    // Map locations to create copy of location with 'disabled' property updated
    // PowerSelect checks 'disabled' by default and grays out options
    // disabled_to_employees_at (not 'disabled') controls employee visibility
    return locations.map((location) => ({
      location,
      disabled: !!location.disabledToEmployeesAt,
    }));
  }

  get selectedLocation(): LocationOption | null {
    if (!this.selectedLocationId) return null;
    const options = this.availableLocations;

    return options.find((option) => option.location.id === this.selectedLocationId) || null;
  }

  get shouldShowToggles(): boolean {
    return !!this.selectedLocationId;
  }

  get isInactivityTimeoutEnabled(): boolean {
    return !!this.state.features?.canAccessInactivityLogout;
  }

  get isPresenceControlCenterEnabled(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.ACCOUNT_MANAGEMENT_PRODUCT_SETTINGS_READ);
  }

  get isGeofenceRadiusEnabled(): boolean {
    return this.featureFlags.isEnabled('presence-control-center-geofence-radius');
  }

  // Bounds + default re-exposed to the template — keeps the values in a single source
  // (utils/geofence-radius-bounds.ts) and out of the .hbs file.
  get geofenceRadiusMin(): number {
    return GEOFENCE_RADIUS_MIN;
  }

  get geofenceRadiusMax(): number {
    return GEOFENCE_RADIUS_MAX;
  }

  get geofenceRadiusDefault(): number {
    return GEOFENCE_RADIUS_DEFAULT;
  }

  get showMacAddressMappings(): boolean {
    return this.abilities.can('view mac-address-mapping');
  }

  get companyId(): string {
    return this.state.currentCompany?.id ?? '';
  }

  get allControlsEnabled(): boolean {
    return (
      this.badgeEnabled &&
      this.wifiEnabled &&
      this.ssoEnabled &&
      this.geoLocationEnabled &&
      this.manualEnabled &&
      this.mdmEnabled
    );
  }

  get absenceOfSignalsDurationOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 3600000, label: '1 hour' },
      { value: 7200000, label: '2 hours' },
      { value: 14400000, label: '4 hours' },
      { value: 28800000, label: '8 hours' },
    ];
  }

  get selectedAbsenceOfSignalsDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.absenceOfSignalsDuration);
  }

  get selectedBadgeDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.badgeAutoSignOutDuration);
  }

  get selectedWifiDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.wifiAutoSignOutDuration);
  }

  get selectedSsoDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.ssoAutoSignOutDuration);
  }

  get selectedMdmDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.mdmAutoSignOutDuration);
  }

  displayTimeoutPeriod(timeout: number): string {
    return MILLISECONDS_TO_TIME_MAP[timeout] || '';
  }

  @action
  openInactivityBox(): void {
    this.isInactivityBoxOpen = true;
  }

  @action
  closeInactivityBox(): void {
    this.isInactivityBoxOpen = false;
    this.inactivityTimeout = this.model?.sessionInactivityTimeout || null;
  }

  @action
  togglePresenceControlCenter(): void {
    this.isPresenceControlCenterOpen = !this.isPresenceControlCenterOpen;
  }

  @action
  selectPresenceTab(tabName: 'sign-in' | 'sign-out'): void {
    this.selectedPresenceTab = tabName;
  }

  @action
  async selectLocation(option: LocationOption): Promise<void> {
    this.selectedLocationId = option.location.id;
    await this.loadPresenceSettings(this.selectedLocationId);
  }

  @action
  async toggleBadge(enabled: boolean): Promise<void> {
    this.badgeEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async toggleWifi(enabled: boolean): Promise<void> {
    this.wifiEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async toggleSSO(enabled: boolean): Promise<void> {
    this.ssoEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async toggleGeoLocation(enabled: boolean): Promise<void> {
    this.geoLocationEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async toggleManual(enabled: boolean): Promise<void> {
    this.manualEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async toggleMDM(enabled: boolean): Promise<void> {
    this.mdmEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async toggleAllControls(enabled: boolean): Promise<void> {
    this.badgeEnabled = enabled;
    this.wifiEnabled = enabled;
    this.ssoEnabled = enabled;
    this.geoLocationEnabled = enabled;
    this.manualEnabled = enabled;
    this.mdmEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async toggleAbsenceOfSignals(enabled: boolean): Promise<void> {
    this.absenceOfSignalsEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async selectAbsenceOfSignalsDuration(option: { value: number; label: string }): Promise<void> {
    this.absenceOfSignalsDuration = option.value;
    await this.savePresenceSettings();
  }

  @action
  async toggleBadgeAutoSignOut(enabled: boolean): Promise<void> {
    this.badgeAutoSignOutEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async selectBadgeDuration(option: { value: number; label: string }): Promise<void> {
    this.badgeAutoSignOutDuration = option.value;
    await this.savePresenceSettings();
  }

  @action
  async toggleWifiAutoSignOut(enabled: boolean): Promise<void> {
    this.wifiAutoSignOutEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async selectWifiDuration(option: { value: number; label: string }): Promise<void> {
    this.wifiAutoSignOutDuration = option.value;
    await this.savePresenceSettings();
  }

  @action
  async toggleSsoAutoSignOut(enabled: boolean): Promise<void> {
    this.ssoAutoSignOutEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async selectSsoDuration(option: { value: number; label: string }): Promise<void> {
    this.ssoAutoSignOutDuration = option.value;
    await this.savePresenceSettings();
  }

  @action
  async toggleMdmAutoSignOut(enabled: boolean): Promise<void> {
    this.mdmAutoSignOutEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  async selectMdmDuration(option: { value: number; label: string }): Promise<void> {
    this.mdmAutoSignOutDuration = option.value;
    await this.savePresenceSettings();
  }

  @action
  async toggleGeoLocationAutoSignOut(enabled: boolean): Promise<void> {
    this.geoLocationAutoSignOutEnabled = enabled;
    await this.savePresenceSettings();
  }

  @action
  validateGeofenceRadius(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value.trim();
    const errorMsg = `Radius must be a whole number between ${GEOFENCE_RADIUS_MIN} and ${GEOFENCE_RADIUS_MAX} meters`;
    if (raw === '' || !/^-?\d+$/.test(raw)) {
      this.geoLocationRadiusError = errorMsg;
      return;
    }
    const value = Number(raw);
    if (value < GEOFENCE_RADIUS_MIN || value > GEOFENCE_RADIUS_MAX) {
      this.geoLocationRadiusError = errorMsg;
      return;
    }
    this.geoLocationRadius = value;
    this.geoLocationRadiusError = null;
  }

  @action
  async saveGeofenceRadius(): Promise<void> {
    if (this.geoLocationRadiusError) return;
    await this.savePresenceSettings();
  }

  enableInactivityTask = dropTask(async () => {
    const companyConfiguration = this.model;
    try {
      if (companyConfiguration) {
        this.inactivityTimeout = 7200000;
        companyConfiguration.sessionInactivityTimeout = this.inactivityTimeout;
        await companyConfiguration.save();

        // start the timer
        await this.inactivity.initialize();
        this.flashMessages.showAndHideFlash('success', 'Inactivity based timeout enabled');
        this.openInactivityBox();
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  disableInactivityTask = dropTask(async () => {
    const companyConfiguration = this.model;
    try {
      if (companyConfiguration) {
        this.inactivityTimeout = null;
        companyConfiguration.sessionInactivityTimeout = this.inactivityTimeout;
        await companyConfiguration.save();

        // stop the timer
        this.inactivity.cleanUp();
        this.flashMessages.showAndHideFlash('success', 'Inactivity based timeout disabled');
        this.isInactivityBoxOpen = false;
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  saveInactivityTask = dropTask(async () => {
    const companyConfiguration = this.model;
    try {
      if (companyConfiguration) {
        companyConfiguration.sessionInactivityTimeout = this.inactivityTimeout;
        await companyConfiguration.save();
        // change the timeout duration
        this.inactivity.timeoutDuration = this.inactivityTimeout;
        this.flashMessages.showAndHideFlash('success', 'Inactivity timeout period updated');
        this.isInactivityBoxOpen = false;
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  async loadPresenceSettings(locationId: string): Promise<void> {
    if (!locationId) return;

    try {
      this.isLoading = true;
      this.error = null;

      const url = urlBuilder.presenceSettingsUrl(locationId);

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const response = (await this.ajax.request(url, {
        method: 'GET',
      })) as PresenceSettingsResponse;

      // Map API response to UI state
      const settings = response.data?.attributes?.settings || {};

      this.badgeEnabled = settings.badge?.enabled ?? true;
      this.wifiEnabled = settings.wifi?.enabled ?? true;
      this.ssoEnabled = settings.sso?.enabled ?? true;
      this.geoLocationEnabled = settings['geo-location']?.enabled ?? true;
      this.manualEnabled = settings.manual?.enabled ?? true;
      this.mdmEnabled = settings.mdm?.enabled ?? true;

      // Geofence radius — only overwrite the tracked default when the API surfaces a real
      // value. `null` (column unset on the server) means "use the display default 150".
      const apiRadius = settings['geo-location']?.['geofence-radius-meters'];
      if (apiRadius != null) {
        this.geoLocationRadius = apiRadius;
      } else {
        this.geoLocationRadius = GEOFENCE_RADIUS_DEFAULT;
      }
      this.geoLocationRadiusError = null;

      // Load sign-out settings
      const signoutSettings = response.data?.attributes?.['signout-settings'];
      const signalTypeAbsence = signoutSettings?.['signal-type-absence'];
      const signalAbsenceMinutes = signoutSettings?.['signal-absence-minutes'];

      // Load aggregated settings (independent of per-signal-type)
      if (signalAbsenceMinutes != null) {
        this.absenceOfSignalsEnabled = signoutSettings?.enabled ?? false;
        this.absenceOfSignalsDuration = signalAbsenceMinutes * 60000;
      } else {
        this.absenceOfSignalsEnabled = false;
        this.absenceOfSignalsDuration = 3600000;
      }

      // Load per-signal-type settings (independent of aggregated)
      if (signalTypeAbsence) {
        this.badgeAutoSignOutEnabled = signalTypeAbsence.badge != null;
        this.badgeAutoSignOutDuration = signalTypeAbsence.badge ? signalTypeAbsence.badge * 60000 : 3600000;

        this.wifiAutoSignOutEnabled = signalTypeAbsence.wifi != null;
        this.wifiAutoSignOutDuration = signalTypeAbsence.wifi ? signalTypeAbsence.wifi * 60000 : 3600000;

        this.ssoAutoSignOutEnabled = signalTypeAbsence.sso != null;
        this.ssoAutoSignOutDuration = signalTypeAbsence.sso ? signalTypeAbsence.sso * 60000 : 3600000;

        this.mdmAutoSignOutEnabled = signalTypeAbsence.mdm != null;
        this.mdmAutoSignOutDuration = signalTypeAbsence.mdm ? signalTypeAbsence.mdm * 60000 : 3600000;

        this.geoLocationAutoSignOutEnabled = signalTypeAbsence['geo-location'] != null;
      } else {
        // Default to OFF - user must opt-in to per-signal-type
        this.badgeAutoSignOutEnabled = false;
        this.badgeAutoSignOutDuration = 3600000;

        this.wifiAutoSignOutEnabled = false;
        this.wifiAutoSignOutDuration = 3600000;

        this.ssoAutoSignOutEnabled = false;
        this.ssoAutoSignOutDuration = 3600000;

        this.mdmAutoSignOutEnabled = false;
        this.mdmAutoSignOutDuration = 3600000;

        this.geoLocationAutoSignOutEnabled = false;
      }
    } catch (_error) {
      this.error = 'Failed to load presence settings';
      this.flashMessages.showAndHideFlash('error', 'Failed to load presence settings');
      // Reset to defaults on error
      this.badgeEnabled = false;
      this.wifiEnabled = false;
      this.ssoEnabled = false;
      this.geoLocationEnabled = false;
      this.manualEnabled = false;
      this.mdmEnabled = false;

      this.badgeAutoSignOutEnabled = false;
      this.badgeAutoSignOutDuration = 3600000;
      this.wifiAutoSignOutEnabled = false;
      this.wifiAutoSignOutDuration = 3600000;
      this.ssoAutoSignOutEnabled = false;
      this.ssoAutoSignOutDuration = 3600000;
      this.mdmAutoSignOutEnabled = false;
      this.mdmAutoSignOutDuration = 3600000;
      this.geoLocationAutoSignOutEnabled = false;

      this.absenceOfSignalsEnabled = false;
      this.absenceOfSignalsDuration = 3600000;

      this.geoLocationRadius = GEOFENCE_RADIUS_DEFAULT;
      this.geoLocationRadiusError = null;
    } finally {
      this.isLoading = false;
    }
  }

  async savePresenceSettings(): Promise<void> {
    if (!this.selectedLocationId) return;

    // Block save while the radius input is in an error state. The flag-gated input is the
    // only path that sets this error, so flag-off / non-PCC saves are unaffected. Surface a
    // flash so toggling WiFi/Badge/SSO/MDM doesn't silently desync from the server.
    if (this.isGeofenceRadiusEnabled && this.geoLocationRadiusError) {
      this.flashMessages.showAndHideFlash('error', 'Fix the sign-in radius before saving other changes');
      return;
    }

    try {
      this.error = null;

      // The PUT path is full-replacement: every save must include the current radius value
      // on the geo-location entry, or a previously-stored value would be wiped on the next
      // save. When the flag is off, we omit the field entirely so we don't leak it to
      // environments where the feature isn't enabled.
      const geoLocationEntry: { enabled: boolean; schedule: null; 'geofence-radius-meters'?: number } = {
        enabled: this.geoLocationEnabled,
        schedule: null,
      };
      if (this.isGeofenceRadiusEnabled) {
        geoLocationEntry['geofence-radius-meters'] = this.geoLocationRadius;
      }

      const settings = {
        badge: { enabled: this.badgeEnabled, schedule: null },
        wifi: { enabled: this.wifiEnabled, schedule: null },
        sso: { enabled: this.ssoEnabled, schedule: null },
        'geo-location': geoLocationEntry,
        manual: { enabled: this.manualEnabled, schedule: null },
        mdm: { enabled: this.mdmEnabled, schedule: null },
      };

      // Build signal-type-absence object (only include if enabled)
      const signalTypeAbsence: { wifi?: number; badge?: number; sso?: number; mdm?: number; 'geo-location'?: number } =
        {};

      if (this.badgeAutoSignOutEnabled) {
        signalTypeAbsence.badge = Math.floor(this.badgeAutoSignOutDuration / 60000);
      }
      if (this.wifiAutoSignOutEnabled) {
        signalTypeAbsence.wifi = Math.floor(this.wifiAutoSignOutDuration / 60000);
      }
      if (this.ssoAutoSignOutEnabled) {
        signalTypeAbsence.sso = Math.floor(this.ssoAutoSignOutDuration / 60000);
      }
      if (this.mdmAutoSignOutEnabled) {
        signalTypeAbsence.mdm = Math.floor(this.mdmAutoSignOutDuration / 60000);
      }
      if (this.geoLocationAutoSignOutEnabled) {
        signalTypeAbsence['geo-location'] = 60; // Hardcoded to 1 hour; unused by entry-recorder for active sign-out
      }

      const anyPerSignalTypeEnabled =
        this.badgeAutoSignOutEnabled ||
        this.wifiAutoSignOutEnabled ||
        this.ssoAutoSignOutEnabled ||
        this.mdmAutoSignOutEnabled ||
        this.geoLocationAutoSignOutEnabled;
      const anyAutoSignOutEnabled = this.absenceOfSignalsEnabled || anyPerSignalTypeEnabled;

      const signoutSettings = {
        enabled: anyAutoSignOutEnabled,
        'signal-absence-minutes': this.absenceOfSignalsEnabled
          ? Math.floor(this.absenceOfSignalsDuration / 60000)
          : null,
        'signal-type-absence': Object.keys(signalTypeAbsence).length > 0 ? signalTypeAbsence : null,
      };

      const url = urlBuilder.presenceSettingsUrl(this.selectedLocationId);
      await this.ajax.request(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          data: {
            type: 'presence-settings',
            attributes: {
              settings,
              'signout-settings': signoutSettings,
            },
          },
        },
      });
      this.flashMessages.showAndHideFlash('success', 'Presence settings saved');
    } catch (_error) {
      this.error = 'Failed to save presence settings';
      this.flashMessages.showAndHideFlash('error', 'Failed to save presence settings');
    }
  }
}

export default LocationOverviewManageAccountSettingsController;
